import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export function useAuthForm(mode: "login" | "register", onEmailChange?: (email: string) => void) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn, signUp } = useAuth();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setEmailError("Please enter a valid email");
    } else {
      setEmailError("");
    }
  };

  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail);
    setEmailError("");
    onEmailChange?.(newEmail);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email");
      return;
    }

    if (!password || (mode === "register" && password.length < 6)) {
      toast({
        title: "Invalid password",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      if (mode === "login") {
        const response = await signIn(email, password);
        if (response.error) throw response.error;
        navigate("/matches");
      } else {
        const response = await signUp(email, password);
        if (response.error) {
          if (response.error.message?.includes("not authorized")) {
            throw new Error("This email domain is not authorized for registration. Please use an authorized email address.");
          }
          throw response.error;
        }
        navigate("/profile");
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      
      let errorMessage = "An error occurred during the process";
      
      if (error.message?.includes("not authorized")) {
        errorMessage = "This email domain is not authorized for registration. Please use an authorized email address.";
      } else if (error.message?.includes("already exists")) {
        errorMessage = "This email is already registered. Please log in.";
      } else if (error.message?.includes("invalid email")) {
        errorMessage = "Invalid email. Please check the email entered.";
      } else if (error.message?.includes("weak password")) {
        errorMessage = "Password is too weak. Use at least 6 characters with letters and numbers.";
      } else if (error.message?.includes("network")) {
        errorMessage = "Connection error. Please check your internet and try again.";
      }

      toast({
        title: "Error in " + (mode === "login" ? "login" : "registration"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    email,
    emailError,
    password,
    stayLoggedIn,
    isLoading,
    handleEmailBlur,
    handleEmailChange,
    setPassword,
    setStayLoggedIn,
    handleSubmit,
  };
}